// 固定资产处置流程 | 前端模型（对应后端 EmArchFixedAssetDisposalVo / QueryForm / SaveForm）
// Author Liang Gangqiang
// Date 2026-09-07
//
// 说明：
//  - gridFields() 是待办 / 列表页（em-table + em-filter，emWorkflowListMixin）的列定义，
//    字段名与 EmArchFixedAssetDisposalVo 一一对应；
//  - filter 的键必须是「后端 QueryForm 字段名 + Filter」
//    （JsonPropertyNamingStrategyForFilterOfFrontEnd 会把 xxxFilter 映射回 xxx），
//    区间字段用后端已定义的 writeOffTimeRte / writeOffTimeLte / applyTimeRte / applyTimeLte；
//  - 查询页走的是 QueryView（EmArchFixedAssetDisposal.jsc-import.json），不用本文件；
//  - 发起页 EmArchFixedAssetDisposalEdit.vue 是自定义表单，也不用 entityFormConfigs，
//    这里保留最小一份，避免 em-table 在非 inlineActionsDisabled 模式下取到 undefined；
//  - 处置类型 / 处置意见 / 资产状态的选项写死在本文件（S1 已知约束：三个枚举都是 String code
//    的普通枚举，不实现 EmBaseEnum，用不了 @FrontendConfig(formOptionsEnum=)），
//    后端改枚举时必须同步改这里与 jsc 配置，没有编译期保护。

// EmArchFixedAssetDisposalTypeEnum
const DISPOSAL_TYPE_OPTIONS = [
    {value: 'SCRAP', text: '报废'},
    {value: 'SELL', text: '出售'},
    {value: 'HOLD', text: '暂不处置'}
];

// EmArchFixedAssetDisposalOpinionEnum
const DISPOSAL_OPINION_OPTIONS = [
    {value: 'SCRAP', text: '报废'},
    {value: 'WASTE_HANDLING', text: '废料处理'},
    {value: 'EVALUATED_SALE', text: '评估售卖'}
];

/**
 * 「处置类型 → 可选处置意见」的合法组合（URS 需求_new R71）。
 * 与后端 EmArchFixedAssetDisposalOpinionEnum.allowedBy(type) 是同一张事实表，
 * 前端只做「不给用户选非法项」，真正拦截仍在 S8 的 resolveDisposalOpinion（7132）。
 */
const OPINION_ALLOWED_BY_TYPE = {
    SCRAP: ['SCRAP'],
    SELL: ['WASTE_HANDLING', 'EVALUATED_SALE'],
    // 暂不处置在 BPMN 里直接进 Event_end_hold，根本走不到采购节点
    HOLD: []
};

// EmArchFixedAssetStatusEnum
const ASSET_STATUS_OPTIONS = [
    {value: 'NORMAL', text: '正常'},
    {value: 'DISPOSING', text: '处置中'},
    {value: 'WRITTEN_OFF', text: '已销账'}
];

/**
 * BPMN em_arch_fixed_asset_disposal 的 11 个 userTask + 3 个 EndEvent。
 * 与 Java 侧 EmArchFixedAssetDisposalStatus 是同一份字面量，
 * 查询页的「流程状态」列也用这份静态选项（不 join 运行时的 EM_ACT_PROCESS_STATUS）。
 */
const DISPOSAL_STATUS_OPTIONS = [
    {value: 'task_disposal_init', text: '发起处置申请'},
    {value: 'task_dept_manager_confirm', text: '申请人部门经理审批'},
    {value: 'task_finance_type', text: '财务人员选择处置类型'},
    {value: 'task_purchase_opinion', text: '采购人员选择处置意见'},
    {value: 'task_finance_manager_confirm', text: '财务经理审批'},
    {value: 'task_asset_manager_confirm', text: '资产经理审批'},
    {value: 'task_cm_confirm', text: 'CM管理层审批'},
    {value: 'task_gm_confirm', text: 'GM管理层审批'},
    {value: 'task_logistics_gate_pass', text: '物流处置员审批出门证'},
    {value: 'task_owner_gate_pass', text: '资产负责人审批出门证'},
    {value: 'task_finance_write_off', text: '财务人员销账'},
    {value: 'Event_end_done', text: '处置完成'},
    {value: 'Event_end_hold', text: '暂不处置结束'},
    {value: 'Event_end_cancel', text: '流程结束'}
];

/**
 * 支持批量审批的节点（URS 需求_new R68 / R73 / R75 / R77 / R79 的「可批量选择审批任务」）：
 * 只有 5 个「同意 / 不同意」节点。财务选类型 / 采购选意见 / 两个出门证 / 销账都要逐单录业务字段，
 * 不能批量。
 */
const BATCH_APPROVE_STATUSES = [
    'task_dept_manager_confirm',
    'task_finance_manager_confirm',
    'task_asset_manager_confirm',
    'task_cm_confirm',
    'task_gm_confirm'
];

class EmArchFixedAssetDisposal {
    constructor({
                    id = null,
                    workOrderCode = '',
                    status = '',
                    disposalReason = '',
                    remark = '',
                    attachments = ''
                } = {}) {
        this.id = id;
        this.workOrderCode = workOrderCode;
        this.status = status;
        this.disposalReason = disposalReason;
        this.remark = remark;
        this.attachments = attachments;
    }
}

function newEntity() {
    return new EmArchFixedAssetDisposal();
}

function gridFields() {
    return Object.freeze([
        {
            key: 'id',
            label: 'label.em.asset.emArchFixedAssetDisposal.grid.id',
            sortable: true,
            isDetail: true,
            form: {type: 'input', modalMultiSelectedType: 'string'}
        },
        {
            key: 'workOrderCode',
            label: 'label.em.asset.emArchFixedAssetDisposal.grid.workOrderCode',
            sortable: true,
            form: {type: 'input', modalMultiSelectedType: 'string'}
        },
        {
            key: 'status',
            label: 'label.em.asset.emArchFixedAssetDisposal.grid.status',
            sortable: true,
            isDetail: true,
            form: {type: 'input', modalMultiSelectedType: 'string'}
        },
        {
            key: 'statusName',
            label: 'label.em.asset.emArchFixedAssetDisposal.grid.statusName',
            sortable: true,
            form: {type: 'input', modalMultiSelectedType: 'string'}
        },
        {
            key: 'applicantName',
            label: 'label.em.asset.emArchFixedAssetDisposal.grid.applicantName',
            sortable: true,
            form: {type: 'input', modalMultiSelectedType: 'string'}
        },
        {
            key: 'applyDeptName',
            label: 'label.em.asset.emArchFixedAssetDisposal.grid.applyDeptName',
            sortable: true,
            form: {type: 'input', modalMultiSelectedType: 'string'}
        },
        {
            key: 'applyTime',
            label: 'label.em.asset.emArchFixedAssetDisposal.grid.applyTime',
            sortable: true,
            form: {type: 'datetimepicker', modalMultiSelectedType: 'string'}
        },
        {
            key: 'disposalType',
            label: 'label.em.asset.emArchFixedAssetDisposal.grid.disposalType',
            sortable: true,
            form: {
                type: 'select',
                modalMultiSelectedType: 'string',
                options: DISPOSAL_TYPE_OPTIONS
            }
        },
        {
            key: 'disposalOpinion',
            label: 'label.em.asset.emArchFixedAssetDisposal.grid.disposalOpinion',
            sortable: true,
            form: {
                type: 'select',
                modalMultiSelectedType: 'string',
                options: DISPOSAL_OPINION_OPTIONS
            }
        },
        {
            key: 'gatePassCode',
            label: 'label.em.asset.emArchFixedAssetDisposal.grid.gatePassCode',
            sortable: true,
            form: {type: 'input', modalMultiSelectedType: 'string'}
        },
        {
            key: 'currentHandlers',
            label: 'label.em.asset.emArchFixedAssetDisposal.grid.currentHandlers',
            sortable: true,
            form: {type: 'input', modalMultiSelectedType: 'string'}
        },
        {
            key: 'writeOffTime',
            label: 'label.em.asset.emArchFixedAssetDisposal.grid.writeOffTime',
            sortable: true,
            form: {type: 'datetimepicker', modalMultiSelectedType: 'string'}
        },
        {
            key: 'disposalReason',
            label: 'label.em.asset.emArchFixedAssetDisposal.grid.disposalReason',
            sortable: true,
            form: {type: 'textarea', modalMultiSelectedType: 'string'}
        },
        {
            key: 'remark',
            label: 'label.em.asset.emArchFixedAssetDisposal.grid.remark',
            sortable: true,
            form: {type: 'textarea', modalMultiSelectedType: 'string'}
        },
        {
            key: 'createdTime',
            label: 'label.em.asset.emArchFixedAssetDisposal.grid.createdTime',
            sortable: true,
            isDetail: true,
            form: {type: 'datetimepicker', modalMultiSelectedType: 'string'}
        }
    ])
}

const filter = {
    idFilter: '',
    idListFilter: [],
    workOrderCodeFilter: '',
    statusFilter: '',
    applicantFilter: '',
    applicantNameFilter: '',
    currentHandlersFilter: '',
    disposalTypeFilter: '',
    disposalOpinionFilter: '',
    assetNoFilter: '',
    assetStatusFilter: '',
    writeOffTimeRteFilter: '',
    writeOffTimeLteFilter: '',
    applyTimeRteFilter: '',
    applyTimeLteFilter: '',
    workflowQueryTypeFilter: ''
};

const fields = Object.freeze(Object.keys(newEntity()).map(i => 'entity.' + i));

const filterFields = Object.freeze(Object.keys(filter).map(i => 'filter.' + i));

// URS 需求_new R46–R53 / 验收 #13：销账时间区间、发起时间区间、资产状态、处置类型、当前处理人、固资号
const filterConfigs = [
    {
        key: 'workOrderCodeFilter',
        label: 'label.em.asset.emArchFixedAssetDisposal.filter.workOrderCode',
        disabled: false,
        form: {type: 'input', modalMultiSelectedType: 'string'}
    },
    {
        key: 'statusFilter',
        label: 'label.em.asset.emArchFixedAssetDisposal.filter.status',
        disabled: false,
        form: {
            type: 'entitySelect',
            modalMultiSelectedType: 'string',
            entity: 'emActProcessStatus',
            showLabelSelectOne: true,
            selectFilter: {processBusinessEntityFilter: 'EmArchFixedAssetDisposal', statusFilter: 1}
        }
    },
    {
        key: 'assetNoFilter',
        label: 'label.em.asset.emArchFixedAssetDisposal.filter.assetNo',
        disabled: false,
        form: {type: 'input', modalMultiSelectedType: 'string'}
    },
    {
        key: 'assetStatusFilter',
        label: 'label.em.asset.emArchFixedAssetDisposal.filter.assetStatus',
        disabled: false,
        form: {
            type: 'select',
            modalMultiSelectedType: 'string',
            showLabelSelectOne: true,
            options: ASSET_STATUS_OPTIONS
        }
    },
    {
        key: 'disposalTypeFilter',
        label: 'label.em.asset.emArchFixedAssetDisposal.filter.disposalType',
        disabled: false,
        form: {
            type: 'select',
            modalMultiSelectedType: 'string',
            showLabelSelectOne: true,
            options: DISPOSAL_TYPE_OPTIONS
        }
    },
    {
        key: 'disposalOpinionFilter',
        label: 'label.em.asset.emArchFixedAssetDisposal.filter.disposalOpinion',
        disabled: false,
        form: {
            type: 'select',
            modalMultiSelectedType: 'string',
            showLabelSelectOne: true,
            options: DISPOSAL_OPINION_OPTIONS
        }
    },
    {
        key: 'applicantNameFilter',
        label: 'label.em.asset.emArchFixedAssetDisposal.filter.applicantName',
        disabled: false,
        form: {type: 'input', modalMultiSelectedType: 'string'}
    },
    {
        key: 'currentHandlersFilter',
        label: 'label.em.asset.emArchFixedAssetDisposal.filter.currentHandlers',
        disabled: false,
        form: {type: 'input', modalMultiSelectedType: 'string'}
    },
    {
        key: 'applyTimeRteFilter',
        label: 'label.em.asset.emArchFixedAssetDisposal.filter.applyTimeRte',
        disabled: false,
        form: {type: 'datetimepicker', modalMultiSelectedType: 'string'}
    },
    {
        key: 'applyTimeLteFilter',
        label: 'label.em.asset.emArchFixedAssetDisposal.filter.applyTimeLte',
        disabled: false,
        form: {type: 'datetimepicker', modalMultiSelectedType: 'string'}
    },
    {
        key: 'writeOffTimeRteFilter',
        label: 'label.em.asset.emArchFixedAssetDisposal.filter.writeOffTimeRte',
        disabled: false,
        form: {type: 'datetimepicker', modalMultiSelectedType: 'string'}
    },
    {
        key: 'writeOffTimeLteFilter',
        label: 'label.em.asset.emArchFixedAssetDisposal.filter.writeOffTimeLte',
        disabled: false,
        form: {type: 'datetimepicker', modalMultiSelectedType: 'string'}
    }
];

// 发起 / 修改走自定义页面 EmArchFixedAssetDisposalEdit.vue，这里只留备注
const entityFormConfigs = [
    {
        key: 'remark',
        label: 'label.em.asset.emArchFixedAssetDisposal.entity.remark',
        sortable: true,
        disabled: false,
        form: {type: 'textarea', modalMultiSelectedType: 'string'}
    }
];

export {
    EmArchFixedAssetDisposal,
    newEntity,
    gridFields,
    fields,
    filterFields,
    filter,
    filterConfigs,
    entityFormConfigs,
    DISPOSAL_TYPE_OPTIONS,
    DISPOSAL_OPINION_OPTIONS,
    OPINION_ALLOWED_BY_TYPE,
    ASSET_STATUS_OPTIONS,
    DISPOSAL_STATUS_OPTIONS,
    BATCH_APPROVE_STATUSES
}
